import { useState, useCallback, useRef } from "react";
import { sendChatMessage, saveConversation } from "../services/chatApi";

function stripArtifactMarkers(text) {
  let clean = text.replace(
    /<!--\s*ARTIFACT\s*:\s*\w+\s*:\s*.+?\s*-->\s*\n?/g,
    "",
  );
  clean = clean.replace(/\n?\s*<!--\s*\/\s*ARTIFACT\s*-->/g, "");
  clean = clean.replace(/\[ARTIFACT_BADGE:art-\d+:\w+:[^\]]+\]/g, "");
  const partial = clean.indexOf("<!--");
  if (partial !== -1) {
    clean = clean.substring(0, partial);
  }
  return clean;
}

function stripResidualHtmlMarkers(text) {
  let clean = text.replace(
    /<!--\s*ARTIFACT\s*:\s*\w+\s*:\s*.+?\s*-->\s*\n?/g,
    "",
  );
  clean = clean.replace(/\n?\s*<!--\s*\/\s*ARTIFACT\s*-->/g, "");
  const partial = clean.indexOf("<!--");
  if (partial !== -1) {
    clean = clean.substring(0, partial);
  }
  return clean;
}

let messageIdCounter = 0;
const nextId = () => `msg-${++messageIdCounter}`;

function generateSessionId() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function buildHistory(messages) {
  return messages
    .filter((m) => !m.isError)
    .map((m) => ({
      role: m.role,
      content: m.content,
    }));
}

export default function useChat() {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [thinkingSteps, setThinkingSteps] = useState([]);
  const [selectedArtifact, setSelectedArtifact] = useState(null);
  const [streamingContent, setStreamingContent] = useState("");
  const abortRef = useRef(false);
  const pendingRef = useRef({});
  const messagesRef = useRef([]);
  const abortControllerRef = useRef(null);
  const sessionIdRef = useRef(generateSessionId());

  const sendMessage = useCallback(
    async (text, filterContext) => {
      if (!text.trim() || isLoading) return;

      const userMessage = {
        id: nextId(),
        role: "user",
        content: text.trim(),
        timestamp: new Date(),
      };

      const updatedMessages = [...messagesRef.current, userMessage];
      messagesRef.current = updatedMessages;
      setMessages(updatedMessages);
      setIsLoading(true);
      setThinkingSteps(["Enviando..."]);
      setStreamingContent("");
      abortRef.current = false;
      pendingRef.current = { content: "", artifacts: [], sources: [] };

      const controller = new AbortController();
      abortControllerRef.current = controller;

      const history = buildHistory(messagesRef.current);

      await sendChatMessage(
        text.trim(),
        history,
        {
          filterContext: filterContext || null,
          onThinkingStep: (step) => {
            if (!abortRef.current) {
              setThinkingSteps((prev) => [...prev, step]);
            }
          },
          onContentDelta: (delta) => {
            if (!abortRef.current) {
              pendingRef.current.content += delta;
              setStreamingContent(
                stripArtifactMarkers(pendingRef.current.content),
              );
              setThinkingSteps([]);
            }
          },
          onContentReset: () => {
            if (!abortRef.current) {
              pendingRef.current.content = "";
              setStreamingContent("");
            }
          },
          onContent: (content) => {
            if (!abortRef.current) {
              pendingRef.current.content = content;
              setStreamingContent(content);
            }
          },
          onArtifacts: (artifacts) => {
            if (!abortRef.current) {
              pendingRef.current.artifacts = artifacts;
            }
          },
          onSources: (sources) => {
            if (!abortRef.current) {
              pendingRef.current.sources = sources;
            }
          },
          onDone: (doneData) => {
            if (!abortRef.current) {
              const { content, artifacts, sources } = pendingRef.current;
              const finalContent =
                artifacts?.length > 0
                  ? stripResidualHtmlMarkers(content)
                  : stripArtifactMarkers(content);
              const usageLogId = doneData?.usage?.usage_log_id ?? null;
              const assistantMessage = {
                id: nextId(),
                role: "assistant",
                content: finalContent,
                artifacts: artifacts || [],
                sources: sources || [],
                usageLogId,
                timestamp: new Date(),
              };

              const withAssistant = [...messagesRef.current, assistantMessage];
              messagesRef.current = withAssistant;
              setMessages(withAssistant);
              setStreamingContent("");

              if (artifacts?.length === 1) {
                setSelectedArtifact(artifacts[0]);
              }

              setIsLoading(false);
              setThinkingSteps([]);

              const saveMsgs = messagesRef.current
                .filter((m) => !m.isError)
                .map((m) => ({
                  role: m.role,
                  content: m.content,
                  timestamp: m.timestamp?.toISOString(),
                  usageLogId: m.usageLogId || undefined,
                }));
              saveConversation(sessionIdRef.current, saveMsgs).catch(() => {});
            }
          },
          onError: (error) => {
            console.error("Chat error:", error);
            if (!abortRef.current) {
              const errorMessage = {
                id: nextId(),
                role: "assistant",
                content: error?.message || "Error desconocido",
                isError: true,
                artifacts: [],
                sources: [],
                timestamp: new Date(),
              };
              messagesRef.current = [...messagesRef.current, errorMessage];
              setMessages(messagesRef.current);
              setStreamingContent("");
              setIsLoading(false);
              setThinkingSteps([]);
            }
          },
        },
        controller.signal,
      );

      abortControllerRef.current = null;
    },
    [isLoading],
  );

  const stopStreaming = useCallback(() => {
    if (!isLoading) return;

    abortRef.current = true;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    const partialContent = pendingRef.current.content || "";
    if (partialContent.trim()) {
      const assistantMessage = {
        id: nextId(),
        role: "assistant",
        content: stripArtifactMarkers(partialContent),
        artifacts: pendingRef.current.artifacts || [],
        sources: pendingRef.current.sources || [],
        timestamp: new Date(),
        stopped: true,
      };
      const withAssistant = [...messagesRef.current, assistantMessage];
      messagesRef.current = withAssistant;
      setMessages(withAssistant);
    }

    setStreamingContent("");
    setIsLoading(false);
    setThinkingSteps([]);
  }, [isLoading]);

  const selectArtifact = useCallback((artifact) => {
    setSelectedArtifact(artifact);
  }, []);

  const clearArtifact = useCallback(() => {
    setSelectedArtifact(null);
  }, []);

  const resetChat = useCallback(() => {
    abortRef.current = true;
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    messagesRef.current = [];
    setMessages([]);
    setIsLoading(false);
    setThinkingSteps([]);
    setSelectedArtifact(null);
    setStreamingContent("");
    sessionIdRef.current = generateSessionId();
  }, []);

  return {
    messages,
    isLoading,
    thinkingSteps,
    selectedArtifact,
    streamingContent,
    sendMessage,
    stopStreaming,
    selectArtifact,
    clearArtifact,
    resetChat,
  };
}
